import {
  BackgroundVideo,
  Button,
  DinarReceiptIcon,
  Logo,
  Overlay,
  Typography,
} from "@/components/atoms";
import {
  BuildingIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldIcon,
} from "@/components/atoms/icons";
import { CircleFilledIcon } from "@/components/atoms/icons/CircleFilledIcon";
import { StarHalfIcon } from "@/components/atoms/icons/StarHalfIcon";
import { StarIcon } from "@/components/atoms/icons/StarIcon";
import { FontAwesome6 } from "@expo/vector-icons";
import { MessageOkDialog } from "@/components/molecules/dialogs/MessageOkDialog";
import { FullScreenLoader } from "@/components/molecules/FullScreenLoader";
import { LogoHeader } from "@/components/molecules/header/LogoHeader";
import { NotificationCard } from "@/components/molecules/NotificationCard";
import { LoginDialog, LoginFlowStep } from "@/components/organisms/LoginDialog.web";
import { WEB_LINKS } from "@/core/constants/webLinks";
import { useAuth } from "@/core/contexts/AuthContext";
import { useLoginConfig, useTranslation, useUIColors, useWebViewport } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { useAppStore } from "@/core/stores/appStore";
import { getResponsiveValue } from "@/core/utils/responsive";
import { getShadowStyle } from "@/core/utils/shadowStyles";
import { ADHA_UAE_PASS_MESSAGE_KEYS } from "@/modules/auth/constants/adhaUaePassError.map";
import { useAdhaLogin } from "@/modules/auth/hooks/useAdhaLogin";
import { useAdhaWelcomeOAuthCallback } from "@/modules/auth/hooks/useAdhaWelcomeOAuthCallback";
import { useLogin } from "@/modules/auth/hooks/useLogin";
import { adhaUaePassService } from "@/modules/auth/services/adhaUaePassService";
import { peekSuspendedAuthFlow } from "@/modules/auth/utils/authFlowState";
import {
  clearAdhaDeepLink,
  restoreAdhaDeepLinkAfterUaePass,
  stashAdhaDeepLinkForUaePass,
} from "@/modules/properties-web/utils/adhaDeepLinkRedirect";
import { BLOOM_UNIVERSAL_HUB_PATH } from "@/modules/bloom-universal/constants/routes";
import { usePasscode } from "@/modules/passcode";
import { useWelcomeWebAuthRedirect } from "@/modules/welcome/hooks/useWelcomeWebAuthRedirect";
import { useWelcomeWebOpenLoginFromQuery } from "@/modules/welcome/hooks/useWelcomeWebOpenLoginFromQuery";
import {
  clearWelcomeLoginQueryFromUrl,
  setWelcomeEmiratesIdQuery,
  setWelcomeLoginQuery,
} from "@/modules/welcome/utils/welcomeLoginQuery";
import {
  clearWelcomeLoginDialogKeep,
  clearWelcomeLoginQueryDismissed,
  ensureWelcomeLoginDialogKeep,
  getWelcomeLoginDialogStep,
  markWelcomeLoginQueryDismissed,
  setWelcomeLoginDialogStep,
  shouldKeepWelcomeLoginDialogOpen,
  type WelcomeLoginDialogStep,
} from "@/modules/welcome/utils/welcomeOpenLoginFlag";
import { renderWelcomeWebAuthGate } from "@/modules/welcome/utils/welcomeWebAuthGate";
import { handleWelcomeWebLoginPress } from "@/modules/welcome/utils/welcomeWebLoginHandlers";
import { router, useFocusEffect } from "expo-router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Image,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
function resolveFooterSocialUrl(translated: string, fallback: string): string {
  const value = translated.trim();
  if (value.startsWith("https://") || value.startsWith("http://")) {
    return value;
  }
  return fallback;
}

function resolveWelcomeLoginDialogInitialStep(): LoginFlowStep {
  if (getWelcomeLoginDialogStep() === "EMIRATES_ID") {
    return LoginFlowStep.EMIRATES_ID;
  }
  return LoginFlowStep.LOGIN;
}

const HERO_VIDEO_WEB = require("@assets/video/intro-landscape.mp4");
const HERO_PLACEHOLDER = require("@assets/images/welcome-bg.jpg");
const MOBILE_FRAME = require("@assets/images/web-home-mobile.png");

export const WelcomeWeb: React.FC = () => {
  const colors = useUIColors();
  const { t, currentLanguage } = useTranslation();
  const { config: loginConfig, isLoading: loginConfigLoading } = useLoginConfig();
  const { breakpointHeight: viewportHeight, breakpointWidth } = useWebViewport();
  const { isRTL } = useI18nContext();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const {
    login,
    isLoading: loginLoading,
    error: loginError,
    clearError: clearLoginError,
  } = useLogin();
  const {
    loginWithAdha,
    completeAdhaLoginWithCode,
    isLoading: adhaLoginLoading,
    error: adhaLoginError,
    clearError: clearAdhaLoginError,
  } = useAdhaLogin();
  const { checkPasscodeState } = usePasscode();
  const [loginDialogVisible, setLoginDialogVisible] = useState(
    () => shouldKeepWelcomeLoginDialogOpen(),
  );
  /** Freeze step at open so mid-flow parent re-renders cannot reset LoginDialog. */
  const [loginDialogInitialStep, setLoginDialogInitialStep] = useState<LoginFlowStep>(
    () => resolveWelcomeLoginDialogInitialStep(),
  );
  const [adhaErrorDialogVisible, setAdhaErrorDialogVisible] = useState(false);
  const [adhaErrorDialogMessage, setAdhaErrorDialogMessage] = useState('');
  /** Keep global loader up while OAuth callback is resolved / code login runs */
  const [adhaOAuthProcessing, setAdhaOAuthProcessing] = useState(() => {
    const isUaePassReturn = adhaUaePassService.hasPendingRawCallback();
    restoreAdhaDeepLinkAfterUaePass(isUaePassReturn);
    return isUaePassReturn;
  });
  const scrollRef = useRef<ScrollView | null>(null);

  const openLoginDialog = useCallback(() => {
    if (useAppStore.getState().auth.isAuthenticated) {
      return;
    }
    clearWelcomeLoginQueryDismissed();
    ensureWelcomeLoginDialogKeep();
    const step = resolveWelcomeLoginDialogInitialStep();
    setLoginDialogInitialStep(step);
    setLoginDialogVisible(true);
    if (step === LoginFlowStep.EMIRATES_ID) {
      setWelcomeEmiratesIdQuery();
    } else {
      setWelcomeLoginQuery();
    }
  }, []);

  const navigateToUniversal = useCallback(() => {
    clearAdhaDeepLink();
    router.replace(BLOOM_UNIVERSAL_HUB_PATH as never);
  }, []);

  const { resetOAuthCallbackHandled } = useAdhaWelcomeOAuthCallback({
    completeAdhaLoginWithCode,
    setLoginDialogVisible,
    setAdhaErrorDialogMessage,
    setAdhaErrorDialogVisible,
    setAdhaOAuthProcessing,
    clearAdhaLoginError,
  });

  useWelcomeWebAuthRedirect();
  useWelcomeWebOpenLoginFromQuery(openLoginDialog, loginDialogVisible);

  useFocusEffect(
    useCallback(() => {
      if (useAppStore.getState().auth.isAuthenticated) {
        return;
      }
      const state = peekSuspendedAuthFlow();
      if (state?.isSuspended && state.flowType === 'web_login') {
        const restoredStep: WelcomeLoginDialogStep =
          state.webStep === LoginFlowStep.EMIRATES_ID ? 'EMIRATES_ID' : 'LOGIN';
        setWelcomeLoginDialogStep(restoredStep);
        openLoginDialog();
      }
    }, [openLoginDialog]),
  );

  // Responsive positioning for notification cards (match Figma)
  // Top notification: floats to the RIGHT of the phone (use right, negative = outside wrapper)
  // Bottom notification: floats to the LEFT of the phone (use left, negative = outside wrapper)
  const topNotificationRight = getResponsiveValue(-32, -40, -56);
  const bottomNotificationLeft = getResponsiveValue(-32, -40, -56);

  // Responsive layout for MOBILE EXPERIENCE split section
  const mobileContentDirection = getResponsiveValue<"column" | "row">(
    "column",
    "row",
    "row",
  );
  const mobileContentJustify = getResponsiveValue<
    "center" | "space-between" | "space-between"
  >("center", "space-between", "space-between");
  const mobileContentAlignItems = getResponsiveValue<
    "center" | "center" | "center"
  >("center", "center", "center");

  // Responsive alignment for footer
  const footerLinksJustify = getResponsiveValue<
    "center" | "center" | "flex-end"
  >("center", "center", "flex-end");
  const footerTopDirection = getResponsiveValue<"column" | "row">(
    "column",
    "row",
    "row",
  );
  const footerTopAlignItems = getResponsiveValue<
    "center" | "flex-start" | "flex-start"
  >("center", "flex-start", "flex-start");
  const footerTopGap = getResponsiveValue<number>(24, 32, 32);
  const footerAboutWidth = getResponsiveValue<number | "auto">(
    "auto",
    360,
    360,
  );
  const footerAboutAlignItems = getResponsiveValue<
    "center" | "flex-start" | "flex-start"
  >("center", "flex-start", "flex-start");

  // Responsive layout for hero buttons:
  // - Default (SSR / initial render): row with 32px gap (desktop/tablet-friendly)
  // - After mount: adjust based on actual screen size
  //   - Small screens: column (stacked)
  //   - Medium/Large screens: row with 52px gap
  const [heroButtonsDirection, setHeroButtonsDirection] = useState<
    "column" | "row"
  >("row");
  const [heroButtonsGap, setHeroButtonsGap] = useState<number>(32);

  useEffect(() => {
    const direction =
      breakpointWidth < 1366 ? ("column" as const) : ("row" as const);
    const gap = breakpointWidth < 1366 ? 16 : 32;

    setHeroButtonsDirection(direction);
    setHeroButtonsGap(gap);
  }, [breakpointWidth]);

  const closeLoginDialog = useCallback(() => {
    markWelcomeLoginQueryDismissed();
    clearWelcomeLoginDialogKeep();
    setLoginDialogVisible(false);
    clearLoginError();
    clearAdhaLoginError();
    // Skip URL clear after successful login — replaceState fights home redirect.
    if (!useAppStore.getState().auth.isAuthenticated) {
      clearWelcomeLoginQueryFromUrl();
    }
  }, [clearAdhaLoginError, clearLoginError]);

  const handleEmiratesIdPress = useCallback(() => {
    setWelcomeLoginDialogStep('EMIRATES_ID');
    setWelcomeEmiratesIdQuery();
  }, []);

  const authGate = renderWelcomeWebAuthGate(authLoading, isAuthenticated);
  // Block Welcome while auth hydrates or user is already logged in (redirect to home).
  if (authGate !== undefined) {
    return authGate;
  }

  const showAdhaGlobalLoader = adhaOAuthProcessing || adhaLoginLoading;

  const heroHeight = Math.max(500, Math.min(viewportHeight, 740));

  const openFooterSocial = (translationKey: string, fallback: string) => {
    if (typeof window === "undefined") {
      return;
    }
    window.open(
      resolveFooterSocialUrl(t(translationKey as any), fallback),
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleDownloadApp = () => {
    const downloadUrl =
      process.env.EXPO_PUBLIC_WEB_DOWNLOAD_APP_URL ||
      WEB_LINKS.DOWNLOAD_APP_FALLBACK;
    if (typeof window !== "undefined") {
      try {
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
      } catch {
        // no-op fallback
      }
    }
  };

  const handleLogin = () =>
    handleWelcomeWebLoginPress(checkPasscodeState, openLoginDialog);

  const handleLoginSubmit = async (email: string, password: string) => {
    try {
      await login({ email, password });
      // Close dialog on successful login - navigation will happen in useLogin hook
      closeLoginDialog();
    } catch {
      // Error is handled by useLogin hook and passed via loginError
      // Don't close dialog on error - let user retry
    }
  };

  const handleAdhaErrorDialogOk = () => {
    setAdhaErrorDialogVisible(false);
    setAdhaErrorDialogMessage('');
    clearAdhaLoginError();
  };

  const handleContinueWithUAEPass = () => {
    try {
      resetOAuthCallbackHandled();
      clearAdhaLoginError();
      setAdhaErrorDialogVisible(false);
      setAdhaErrorDialogMessage('');
      stashAdhaDeepLinkForUaePass();
      loginWithAdha();
    } catch {
      setAdhaErrorDialogMessage(t(ADHA_UAE_PASS_MESSAGE_KEYS.ERROR_GENERIC));
      setAdhaErrorDialogVisible(true);
    }
  };

  const handleContactSales = () => {
    if (typeof window !== "undefined") {
      try {
        window.location.href = "mailto:customerservice@bloomholding.com";
      } catch {
        // ignore
      }
    }
  };

  return (
    <View style={[styles.root, { backgroundColor: colors.backgroundLevel2 }]}>
      <ScrollView
        ref={scrollRef}
        bounces={false}
        contentContainerStyle={styles.scrollContent}
      >
        {/* HERO SECTION */}
        <View
          style={[
            styles.heroContainer,
            {
              height: heroHeight,
            },
          ]}
        >
          <BackgroundVideo
            localSource={HERO_VIDEO_WEB}
            placeholderImage={HERO_PLACEHOLDER}
            style={styles.heroVideo}
            contentFit="cover"
            loop={true}
          >
            <Overlay type="dark" opacity={0.45} />

            <LogoHeader
              showLanguageSwitch={true}
              backButtonColor="white"
              languageTextColor="white"
              hideLogo={true}
              hideBackIfRoot={false}
              onBackPress={navigateToUniversal}
            />

            <View style={styles.heroContent}>
              {/* Bloom Logo - centered above welcome text */}
              <TouchableOpacity
                style={styles.heroLogoContainer}
                onPress={navigateToUniversal}
                accessibilityRole="button"
                accessibilityLabel={t("welcome.appName")}
                activeOpacity={0.85}
              >
                <Logo
                  key={`hero-logo-${currentLanguage}`}
                  color="white"
                  size="xlarge"
                />
              </TouchableOpacity>

              <Typography
                variant="display"
                color="white"
                style={styles.heroTitle}
              >
                {t("welcome.heroTitle" as any)}
              </Typography>
              <Typography
                variant="body"
                color="white"
                style={styles.heroSubtitle}
              >
                {t("welcome.heroSubtitle" as any)}
              </Typography>

              <View
                style={[
                  styles.heroButtonsRow,
                  { flexDirection: heroButtonsDirection, gap: heroButtonsGap },
                ]}
              >
                <Button
                  title={t("welcome.downloadApp" as any)}
                  variant="primary"
                  fullWidth={false}
                  onPress={handleDownloadApp}
                  theme="dark"
                  style={styles.heroButton}
                />
                <Button
                  title={t("welcome.login" as any)}
                  variant="outline"
                  fullWidth={false}
                  onPress={handleLogin}
                  theme="dark"
                  style={styles.heroButton}
                />
              </View>
            </View>
          </BackgroundVideo>

          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => {
              scrollRef.current?.scrollTo({ y: heroHeight, animated: true });
            }}
            style={styles.heroScrollHint}
          >
            <ChevronDownIcon size={32} color="white" />
          </TouchableOpacity>
        </View>

        {/* FEATURES SECTION */}
        <View
          style={[styles.section, { backgroundColor: colors.backgroundLevel1 }]}
        >
          <View style={styles.sectionHeader}>
            <View
              style={[
                styles.sectionPill,
                { backgroundColor: colors.backgroundLevel3 },
              ]}
            >
              <Typography variant="title3" color="primary">
                {t("webHome.featuresPill" as any)}
              </Typography>
            </View>
            <Typography
              variant="title2"
              color="primary"
              style={styles.sectionTitle}
            >
              {t("webHome.featuresTitle" as any)}
            </Typography>
            <Typography
              variant="body"
              color="tertiary"
              style={styles.sectionSubtitle}
            >
              {t("webHome.featuresSubtitle" as any)}
            </Typography>
          </View>

          <View style={styles.featuresRow}>
            <View
              style={[
                styles.featureCard,
                {
                  borderColor: colors.outlineInactive,
                  alignItems: isRTL ? "flex-end" : "flex-start",
                },
              ]}
            >
              <View style={isRTL ? [styles.featureIconRTL, { marginLeft: "auto" }] : undefined}>
                <BuildingIcon size={32} color={colors.vectorsIconGreyscale} />
              </View>
              <Typography
                variant="title3"
                color="primary"
                style={[
                  styles.featureTitle,
                  isRTL && { textAlign: "right", width: "100%" },
                ]}
              >
                {t("webHome.featuresPropertyManagementTitle" as any)}
              </Typography>
              <Typography
                variant="body"
                color="tertiary"
                style={isRTL && { textAlign: "right", width: "100%" }}
              >
                {t("webHome.featuresPropertyManagementBody" as any)}
              </Typography>
            </View>
            <View
              style={[
                styles.featureCard,
                {
                  borderColor: colors.outlineInactive,
                  alignItems: isRTL ? "flex-end" : "flex-start",
                },
              ]}
            >
              <View style={isRTL ? [styles.featureIconRTL, { marginLeft: "auto" }] : undefined}>
                <DinarReceiptIcon size={32} color={colors.vectorsIconGreyscale} />
              </View>
              <Typography
                variant="title3"
                color="primary"
                style={[
                  styles.featureTitle,
                  isRTL && { textAlign: "right", width: "100%" },
                ]}
              >
                {t("webHome.featuresPaymentTrackingTitle" as any)}
              </Typography>
              <Typography
                variant="body"
                color="tertiary"
                style={isRTL && { textAlign: "right", width: "100%" }}
              >
                {t("webHome.featuresPaymentTrackingBody" as any)}
              </Typography>
            </View>
            <View
              style={[
                styles.featureCard,
                {
                  borderColor: colors.outlineInactive,
                  alignItems: isRTL ? "flex-end" : "flex-start",
                },
              ]}
            >
              <View style={isRTL ? [styles.featureIconRTL, { marginLeft: "auto" }] : undefined}>
                <ShieldIcon size={32} color={colors.vectorsIconGreyscale} />
              </View>
              <Typography
                variant="title3"
                color="primary"
                style={[
                  styles.featureTitle,
                  isRTL && { textAlign: "right", width: "100%" },
                ]}
              >
                {t("webHome.featuresSecurePlatformTitle" as any)}
              </Typography>
              <Typography
                variant="body"
                color="tertiary"
                style={isRTL && { textAlign: "right", width: "100%" }}
              >
                {t("webHome.featuresSecurePlatformBody" as any)}
              </Typography>
            </View>
          </View>
        </View>

        {/* MOBILE EXPERIENCE SECTION */}
        <View
          style={[styles.section, { backgroundColor: colors.backgroundLevel2 }]}
        >
          <View
            style={[
              styles.mobileContent,
              {
                flexDirection: mobileContentDirection,
                justifyContent: mobileContentJustify,
                alignItems: mobileContentAlignItems,
              },
            ]}
          >
            <View style={styles.mobileCopy}>
              <View
                style={[
                  styles.sectionPillLeft,
                  { backgroundColor: colors.backgroundLevel3 },
                ]}
              >
                <Typography variant="title2" color="primary">
                  {t("webHome.mobilePill" as any)}
                </Typography>
              </View>
              <Typography
                variant="largeTitle"
                color="primary"
                style={[
                  styles.sectionTitleLeft,
                  isRTL && { textAlign: "right" },
                ]}
              >
                {t("webHome.mobileTitle" as any)}
              </Typography>
              <Typography
                variant="title2"
                color="tertiary"
                style={[
                  styles.sectionSubtitleLeft,
                  isRTL && { textAlign: "right" },
                ]}
              >
                {t("webHome.mobileSubtitle" as any)}
              </Typography>
              <View style={styles.bulletList}>
                <View style={styles.bulletRow}>
                  <CircleFilledIcon size={8} color={colors.fontTertiary} />
                  <Typography
                    variant="bodyLarge"
                    color="primary"
                    style={styles.bulletText}
                  >
                    {t("webHome.mobileBullet1" as any)}
                  </Typography>
                </View>
                <View style={styles.bulletRow}>
                  <CircleFilledIcon size={8} color={colors.fontTertiary} />
                  <Typography
                    variant="bodyLarge"
                    color="primary"
                    style={styles.bulletText}
                  >
                    {t("webHome.mobileBullet2" as any)}
                  </Typography>
                </View>
                <View style={styles.bulletRow}>
                  <CircleFilledIcon size={8} color={colors.fontTertiary} />
                  <Typography
                    variant="bodyLarge"
                    color="primary"
                    style={styles.bulletText}
                  >
                    {t("webHome.mobileBullet3" as any)}
                  </Typography>
                </View>
                <View style={styles.bulletRow}>
                  <CircleFilledIcon size={8} color={colors.fontTertiary} />
                  <Typography
                    variant="bodyLarge"
                    color="primary"
                    style={styles.bulletText}
                  >
                    {t("webHome.mobileBullet4" as any)}
                  </Typography>
                </View>
              </View>
              <Button
                title={t("webHome.mobileCta" as any)}
                variant="outline"
                fullWidth={false}
                onPress={handleDownloadApp}
                trailing={
                  <View
                    style={isRTL ? { transform: [{ scaleX: -1 }] } : undefined}
                  >
                    <ChevronRightIcon size={16} color={colors.fontPrimary} />
                  </View>
                }
              />
            </View>

            <View style={styles.mobileVisualWrapper}>
              <Image
                source={MOBILE_FRAME}
                style={styles.mobileImage}
                resizeMode="contain"
              />
              <View
                style={[
                  styles.mobileNotificationTop,
                  { right: topNotificationRight },
                ]}
              >
                <NotificationCard
                  title={t("webHome.mobileToast1Title" as any)}
                  body={t("webHome.mobileToast1Body" as any)}
                />
              </View>
              <View
                style={[
                  styles.mobileNotificationBottom,
                  { left: bottomNotificationLeft },
                ]}
              >
                <NotificationCard
                  title={t("webHome.mobileToast2Title" as any)}
                  body={t("webHome.mobileToast2Body" as any)}
                />
              </View>
            </View>
          </View>
        </View>

        {/* TESTIMONIALS SECTION */}
        <View
          style={[styles.section, { backgroundColor: colors.backgroundLevel1 }]}
        >
          <View style={styles.sectionHeader}>
            <View
              style={[
                styles.sectionPill,
                { backgroundColor: colors.backgroundLevel3 },
              ]}
            >
              <Typography variant="title3" color="primary">
                {t("webHome.testimonialsPill" as any)}
              </Typography>
            </View>
            <Typography
              variant="title2"
              color="primary"
              style={styles.sectionTitle}
            >
              {t("webHome.testimonialsTitle" as any)}
            </Typography>
            <Typography
              variant="body"
              color="tertiary"
              style={styles.sectionSubtitle}
            >
              {t("webHome.testimonialsSubtitle" as any)}
            </Typography>
          </View>

          <View style={styles.testimonialsRow}>
            {["card1", "card2", "card3"].map((key, index) => (
              <View
                key={key}
                style={[
                  styles.testimonialCard,
                  getShadowStyle(styles.testimonialCardShadow),
                  { backgroundColor: colors.backgroundLevel2 },
                ]}
              >
                <View style={styles.starsRow}>
                  {index === 1 ? (
                    <>
                      {Array.from({ length: 4 }).map((_, i) => (
                        <StarIcon
                          key={i}
                          size={24}
                          color={colors.vectorsIconGreyscale}
                        />
                      ))}
                      <View
                        style={
                          isRTL ? { transform: [{ scaleX: -1 }] } : undefined
                        }
                      >
                        <StarHalfIcon
                          size={24}
                          color={colors.vectorsIconGreyscale}
                        />
                      </View>
                    </>
                  ) : (
                    Array.from({ length: 5 }).map((_, i) => (
                      <StarIcon
                        key={i}
                        size={24}
                        color={colors.vectorsIconGreyscale}
                      />
                    ))
                  )}
                </View>
                <Typography
                  variant="body"
                  color="primary"
                  style={styles.testimonialBody}
                >
                  {t(
                    `webHome.testimonials${key.charAt(0).toUpperCase() + key.slice(1)}Body` as any,
                  )}
                </Typography>
                <View style={styles.testimonialMeta}>
                  <Typography variant="title3" color="primary">
                    {t(
                      `webHome.testimonials${key.charAt(0).toUpperCase() + key.slice(1)}Name` as any,
                    )}
                  </Typography>
                  <Typography variant="subhead" color="tertiary">
                    {t(
                      `webHome.testimonials${key.charAt(0).toUpperCase() + key.slice(1)}Role` as any,
                    )}
                  </Typography>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* CTA SECTION */}
        <View
          style={[styles.section, { backgroundColor: colors.backgroundLevel3 }]}
        >
          <Typography variant="title2" color={colors.welcomeInk} style={styles.ctaTitle}>
            {t("webHome.ctaTitle" as any)}
          </Typography>
          <Typography
            variant="body"
            color={colors.welcomeInk}
            style={styles.ctaSubtitle}
          >
            {t("webHome.ctaSubtitle" as any)}
          </Typography>
          <Button
            title={t("webHome.ctaButton" as any)}
            variant="primary"
            fullWidth={false}
            onPress={handleContactSales}
            style={styles.ctaButton}
          />
        </View>

        <View
          style={[styles.footer, { backgroundColor: colors.backgroundLevel4 }]}
        >
          {isRTL ? (
            <View
              style={[
                styles.footerTop,
                {
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 32,
                  maxWidth: 1309,
                },
              ]}
            >
              <View style={{ width: 400, gap: 20, alignItems: "flex-start" }}>
                <Logo
                  key={`footer-logo-${currentLanguage}`}
                  color="bloomRed"
                  size="large"
                  style={{ width: 180, height: 64 }}
                />
                <Typography
                  variant="bodyLarge"
                  color={colors.welcomeInk}
                  style={{ textAlign: "right", fontSize: 18 }}
                >
                  {t("webHome.footerAbout" as any)}
                </Typography>
              </View>

              <View style={{ width: 444, paddingVertical: 16, gap: 19 }}>
                <Typography
                  variant="subhead"
                  color={colors.welcomeInk}
                  style={{
                    textAlign: "right",
                    fontWeight: "700",
                    fontSize: 16,
                  }}
                >
                  مشاريعنا
                </Typography>
                <View
                  style={{
                    flexDirection: "row",
                    gap: 24,
                    justifyContent: "flex-end",
                  }}
                >
                  {/* Column 1 (leftmost): الضي, بلووم أرجان من روتانا, سوهو سكوير, بلووم سنترال */}
                  <View
                    style={{ gap: 16, alignItems: "flex-start", width: 154 }}
                  >
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      الضي
                    </Typography>
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      بلووم أرجان من روتانا
                    </Typography>
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      سوهو سكوير
                    </Typography>
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      بلووم سنترال
                    </Typography>
                  </View>
                  <View style={{ gap: 16, alignItems: "flex-start" }}>
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      حدائق بلووم
                    </Typography>
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      بلووم هايتس
                    </Typography>
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      بلووم ليفينج - أبوظبي
                    </Typography>
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      روتشستر، الولايات المتحدة الأمريكية
                    </Typography>
                  </View>
                  <View
                    style={{ gap: 16, alignItems: "flex-start", width: 90 }}
                  >
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      بلووم مارينا
                    </Typography>
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      بلووم تاورز
                    </Typography>
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      فايــة
                    </Typography>
                    <Typography
                      variant="body"
                      color={colors.welcomeInk}
                      style={{ textAlign: "right", fontSize: 14 }}
                    >
                      بارك فيو
                    </Typography>
                  </View>
                </View>
              </View>

              {/* Contact Section - Rightmost in Arabic */}
              <View style={{ width: 273, paddingVertical: 16, gap: 40 }}>
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 24,
                    alignItems: "center",
                  }}
                >
                  <TouchableOpacity
                    onPress={() =>
                      openFooterSocial(
                        "webHome.footerContactFacebookUrl",
                        WEB_LINKS.FACEBOOK,
                      )}
                    accessibilityRole="link"
                    accessibilityLabel={t(
                      "webHome.footerContactFacebook" as any,
                    )}
                  >
                    <FontAwesome6
                      name="facebook-f"
                      brand
                      size={20}
                      color={colors.welcomeInk}
                    />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() =>
                      openFooterSocial(
                        "webHome.footerContactTwitterUrl",
                        WEB_LINKS.TWITTER,
                      )}
                    accessibilityRole="link"
                    accessibilityLabel={t(
                      "webHome.footerContactTwitter" as any,
                    )}
                  >
                    <FontAwesome6
                      name="x-twitter"
                      brand
                      size={20}
                      color={colors.welcomeInk}
                    />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() =>
                      openFooterSocial(
                        "webHome.footerContactInstagramUrl",
                        WEB_LINKS.INSTAGRAM,
                      )}
                    accessibilityRole="link"
                    accessibilityLabel={t(
                      "webHome.footerContactInstagram" as any,
                    )}
                  >
                    <FontAwesome6
                      name="instagram"
                      brand
                      size={20}
                      color={colors.welcomeInk}
                    />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() =>
                      openFooterSocial(
                        "webHome.footerContactLinkedInUrl",
                        WEB_LINKS.LINKEDIN,
                      )}
                    accessibilityRole="link"
                    accessibilityLabel={t(
                      "webHome.footerContactLinkedIn" as any,
                    )}
                  >
                    <FontAwesome6
                      name="linkedin-in"
                      brand
                      size={20}
                      color={colors.welcomeInk}
                    />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() =>
                      openFooterSocial(
                        "webHome.footerContactTikTokUrl",
                        WEB_LINKS.TIKTOK,
                      )}
                    accessibilityRole="link"
                    accessibilityLabel={t(
                      "webHome.footerContactTikTok" as any,
                    )}
                  >
                    <FontAwesome6
                      name="tiktok"
                      brand
                      size={20}
                      color={colors.welcomeInk}
                    />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() =>
                      openFooterSocial(
                        "webHome.footerContactYouTubeUrl",
                        WEB_LINKS.YOUTUBE,
                      )}
                    accessibilityRole="link"
                    accessibilityLabel={t(
                      "webHome.footerContactYouTube" as any,
                    )}
                  >
                    <FontAwesome6
                      name="youtube"
                      brand
                      size={20}
                      color={colors.welcomeInk}
                    />
                  </TouchableOpacity>
                </View>
                <View style={{ gap: 16, alignItems: "flex-start" }}>
                  <Typography
                    variant="subhead"
                    color={colors.welcomeInk}
                    style={{ textAlign: "right", fontSize: 14 }}
                  >
                    تواصل معنا
                  </Typography>
                  <Typography
                    variant="body"
                    color={colors.welcomeInk}
                    style={{ textAlign: "right" }}
                  >
                    80025666
                  </Typography>
                  <View
                    style={{
                      flexDirection: "row",
                      gap: 4,
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body" color={colors.welcomeInk}>
                      +971
                    </Typography>
                    <Typography variant="body" color={colors.welcomeInk}>
                      26969500
                    </Typography>
                    <Typography variant="body" color={colors.welcomeInk}>
                      (من خارج الإمارات)
                    </Typography>
                  </View>
                  <Typography
                    variant="body"
                    color={colors.welcomeInk}
                    style={{ textAlign: "right" }}
                  >
                    customerservice@bloomholding.com
                  </Typography>
                </View>
              </View>
            </View>
          ) : (
            /* English Footer Layout: Logo (left) → Projects + Contact (right) */
            <View
              style={[
                styles.footerTop,
                {
                  flexDirection: footerTopDirection,
                  alignItems: footerTopAlignItems,
                  gap: footerTopGap,
                },
              ]}
            >
              <View
                style={[
                  styles.footerAbout,
                  {
                    width: footerAboutWidth,
                    alignItems: footerAboutAlignItems,
                  },
                ]}
              >
                <Logo
                  key={`footer-logo-${currentLanguage}`}
                  color="bloomRed"
                  size="large"
                />
              </View>
              <View
                style={[
                  styles.footerLinks,
                  { justifyContent: footerLinksJustify },
                ]}
              >
                <View style={styles.footerColumn}>
                  <Typography variant="subhead" color={colors.welcomeInk}>
                    {t("webHome.footerProjectsHeading1" as any)}
                  </Typography>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerProjectsItem1" as any)}
                  </Typography>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerProjectsItem2" as any)}
                  </Typography>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerProjectsItem3" as any)}
                  </Typography>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerProjectsItem4" as any)}
                  </Typography>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerProjectsItem5" as any)}
                  </Typography>
                </View>
                <View style={styles.footerColumn}>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerProjectsItem6" as any)}
                  </Typography>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerProjectsItem7" as any)}
                  </Typography>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerProjectsItem8" as any)}
                  </Typography>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerProjectsItem9" as any)}
                  </Typography>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerProjectsItem10" as any)}
                  </Typography>
                </View>
                <View style={styles.footerColumn}>
                  <Typography variant="subhead" color={colors.welcomeInk}>
                    {t("webHome.footerContactHeading" as any)}
                  </Typography>
                  <View style={styles.footerContactRow}>
                    <TouchableOpacity
                      onPress={() =>
                        openFooterSocial(
                          "webHome.footerContactTwitterUrl",
                          WEB_LINKS.TWITTER,
                        )}
                      accessibilityRole="link"
                      accessibilityLabel={t(
                        "webHome.footerContactTwitter" as any,
                      )}
                    >
                      <FontAwesome6
                        name="x-twitter"
                        brand
                        size={20}
                        color={colors.welcomeInk}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() =>
                        openFooterSocial(
                          "webHome.footerContactFacebookUrl",
                          WEB_LINKS.FACEBOOK,
                        )}
                      accessibilityRole="link"
                      accessibilityLabel={t(
                        "webHome.footerContactFacebook" as any,
                      )}
                    >
                      <FontAwesome6
                        name="facebook-f"
                        brand
                        size={20}
                        color={colors.welcomeInk}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() =>
                        openFooterSocial(
                          "webHome.footerContactInstagramUrl",
                          WEB_LINKS.INSTAGRAM,
                        )}
                      accessibilityRole="link"
                      accessibilityLabel={t(
                        "webHome.footerContactInstagram" as any,
                      )}
                    >
                      <FontAwesome6
                        name="instagram"
                        brand
                        size={20}
                        color={colors.welcomeInk}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() =>
                        openFooterSocial(
                          "webHome.footerContactLinkedInUrl",
                          WEB_LINKS.LINKEDIN,
                        )}
                      accessibilityRole="link"
                      accessibilityLabel={t(
                        "webHome.footerContactLinkedIn" as any,
                      )}
                    >
                      <FontAwesome6
                        name="linkedin-in"
                        brand
                        size={20}
                        color={colors.welcomeInk}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() =>
                        openFooterSocial(
                          "webHome.footerContactTikTokUrl",
                          WEB_LINKS.TIKTOK,
                        )}
                      accessibilityRole="link"
                      accessibilityLabel={t(
                        "webHome.footerContactTikTok" as any,
                      )}
                    >
                      <FontAwesome6
                        name="tiktok"
                        brand
                        size={20}
                        color={colors.welcomeInk}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() =>
                        openFooterSocial(
                          "webHome.footerContactYouTubeUrl",
                          WEB_LINKS.YOUTUBE,
                        )}
                      accessibilityRole="link"
                      accessibilityLabel={t(
                        "webHome.footerContactYouTube" as any,
                      )}
                    >
                      <FontAwesome6
                        name="youtube"
                        brand
                        size={20}
                        color={colors.welcomeInk}
                      />
                    </TouchableOpacity>
                  </View>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerContactPhone1" as any)}
                  </Typography>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerContactPhone2" as any)}
                  </Typography>
                  <Typography variant="body" color={colors.welcomeInk}>
                    {t("webHome.footerContactEmail" as any)}
                  </Typography>
                </View>
              </View>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Login Dialog */}
      {/* Wait for the login config so UAE PASS / Emirates ID buttons don't flash then hide. */}
      <LoginDialog
        visible={loginDialogVisible && !loginConfigLoading}
        onClose={closeLoginDialog}
        onEmiratesIdPress={handleEmiratesIdPress}
        onLogin={handleLoginSubmit}
        onContinueWithUAEPass={handleContinueWithUAEPass}
        isLoading={loginLoading || adhaLoginLoading || adhaOAuthProcessing}
        error={loginError || adhaLoginError}
        isUAEpassLogin={loginConfig?.isUAEpassLogin ?? true}
        isEmiratesIdLogin={loginConfig?.isEmiratesIdLogin ?? true}
        initialStep={loginDialogInitialStep}
      />
      {/* MessageOkDialog: UAE Pass OAuth redirect + ADHA login API mapped errors */}
      <MessageOkDialog
        visible={adhaErrorDialogVisible}
        message={adhaErrorDialogMessage}
        onOk={handleAdhaErrorDialogOk}
      />
      <FullScreenLoader visible={showAdhaGlobalLoader} />
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    backgroundColor: "transparent",
  },
  heroContainer: {
    width: "100%",
    minHeight: 700, // Minimum height for smaller screens
    overflow: "hidden",
    position: "relative",
  },
  heroVideo: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
  },
  heroHeader: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
  },
  headerPlaceholder: {
    width: 60,
    height: 40,
  },
  languageToggle: {
    paddingVertical: 4,
    width: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  heroContent: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
  },
  heroLogoContainer: {
    alignItems: "center", // Center the logo horizontally within container
    justifyContent: "center",
    paddingVertical: 8,
    marginBottom: 40,
    // Container will be centered by parent heroContent's alignItems: 'center'
  },
  heroLogo: {
    width: 220, // Increased size for web hero
    height: 64,
    alignItems: "center",
  },
  heroTitle: {
    textAlign: "center",
    marginBottom: 16,
    fontSize: 48, // Reduced font size per marketing feedback
    fontFamily: "Orpheus-Pro", // Orpheus Pro Regular from assets/fonts
    fontWeight: "400", // Regular weight
  },
  heroSubtitle: {
    textAlign: "center",
    maxWidth: 600,
    marginBottom: 32,
  },
  heroButtonsRow: {
    flexDirection: "row",
    gap: 32,
    alignItems: "center",
  },
  heroButton: {
    width: 200,
  },
  heroScrollHint: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 24,
    height: 64,
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.95,
  },
  section: {
    width: "100%",
    paddingHorizontal: 24,
    paddingVertical: 48,
    alignItems: "center",
  },
  sectionHeader: {
    alignItems: "center",
    marginBottom: 32,
    paddingHorizontal: 16,
  },
  sectionPill: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 22,
    marginBottom: 16,
  },
  sectionPillLeft: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 22,
    marginBottom: 16,
    alignSelf: "flex-start",
  },
  sectionTitle: {
    textAlign: "center",
    marginBottom: 8,
  },
  sectionSubtitle: {
    textAlign: "center",
    maxWidth: 650,
  },
  sectionTitleLeft: {
    textAlign: "left",
    marginTop: 16,
    marginBottom: 8,
  },
  sectionSubtitleLeft: {
    textAlign: "left",
    maxWidth: 700,
    marginBottom: 24,
  },
  featuresRow: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 32,
    width: "100%",
    flexWrap: "wrap",
  },
  featureCard: {
    borderWidth: 1,
    borderRadius: 24,
    paddingVertical: 32,
    paddingHorizontal: 24,
    width: 320,
    gap: 16,
    // alignItems is set dynamically based on isRTL in the component
  },
  featureTitle: {
    marginBottom: 4,
  },
  featureIconRTL: {
    alignSelf: "flex-end",
  },
  mobileContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    maxWidth: 1100,
    gap: 40,
    flexWrap: "wrap",
  },
  mobileCopy: {
    flex: 1,
    minWidth: 280,
  },
  bulletList: {
    marginTop: 16,
    marginBottom: 24,
    gap: 8,
  },
  bulletRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 4,
  },
  bulletText: {
    flexShrink: 1,
  },
  mobileVisualWrapper: {
    width: 360,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    alignSelf: "center",
  },
  mobileImage: {
    width: "100%",
    height: 480,
  },
  mobileNotificationTop: {
    position: "absolute",
    top: 30,
    // Horizontal position is controlled via dynamic `right: topNotificationRight`
    // so the card floats to the right of the phone frame.
    width: 212,
    zIndex: 10,
  },
  mobileNotificationBottom: {
    position: "absolute",
    bottom: 140,
    // Horizontal position is controlled via dynamic `left: bottomNotificationLeft`
    // so the card floats to the left of the phone frame.
    width: 212,
    zIndex: 10,
  },
  testimonialsRow: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 24,
    flexWrap: "wrap",
    width: "100%",
  },
  testimonialCard: {
    borderRadius: 24,
    paddingVertical: 24,
    paddingHorizontal: 16,
    width: 340,
    alignItems: "center",
    gap: 16,
    elevation: 2, // Android shadow
  },
  testimonialCardShadow: {
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 10,
    elevation: 2,
  },
  starsRow: {
    flexDirection: "row",
    gap: 4,
  },
  testimonialBody: {
    textAlign: "center",
    minHeight: 80,
  },
  testimonialMeta: {
    alignItems: "center",
    gap: 4,
  },
  ctaTitle: {
    textAlign: "center",
    marginBottom: 12,
    maxWidth: 700,
  },
  ctaSubtitle: {
    textAlign: "center",
    marginBottom: 24,
    maxWidth: 700,
  },
  footer: {
    width: "100%",
    paddingHorizontal: 24,
    paddingVertical: 40,
  },
  footerTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 32,
  },
  footerAbout: {
    width: 360,
    gap: 16,
    alignItems: "flex-start", // Left-align logo and text
  },
  footerAboutText: {
    textAlign: "left",
  },
  ctaButton: {
    width: 200,
    alignSelf: "center",
  },
  footerLinks: {
    flexDirection: "row",
    gap: 32,
    flexWrap: "wrap",
    flex: 1,
    justifyContent: "flex-end",
  },
  footerColumn: {
    minWidth: 140,
    gap: 8,
  },
  footerContactRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 8,
  },
  footerContactSection: {
    flexDirection: "column",
  },
  footerProjectsSection: {
    flexDirection: "column",
  },
});

export default WelcomeWeb;
